import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Input from '@welldev-group/rcl-input';
import Icon from '@welldev-group/rcl-icon';
import Button from '@welldev-group/rcl-button';
import { Text } from '@welldev-group/rcl-typography';
import Tooltip from '@welldev-group/rcl-tooltip';
import { I18nText, Radio, Spinner } from '@welldev-group/react-comp-lib';
import Alert from '@welldev-group/rcl-alert';
import Modal from '@welldev-group/rcl-modal';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';

import Amount from './Amount';
import CreditCard from './CreditCard';

const PrimaryShapeStringAndBool = PropTypes.oneOfType([PropTypes.string, PropTypes.bool]);
const PrimaryShapeStringAndNumber = PropTypes.oneOfType([PropTypes.string, PropTypes.number]);

const TopUpModal = ({
  currentBalance,
  currency,
  affiliateId,
  disableCreditCardOption,
  primaryCreditCard,
  creditCards,
  stripeSuccessStatus,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [amountError, setAmountError] = useState(null);
  const [stripeError, setStripeError] = useState(null);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [paymentBy, setPaymentBy] = useState('');
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [iframeSource, setIframeSource] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const creditCardSelected = paymentBy === 'credit_card';
  const wireTransferSelected = paymentBy === 'wire_transfer';
  const selectedCard = creditCards.find(card => card.id === selectedCardId);
  const otherCards = creditCards.filter(card => card.id !== selectedCardId);

  const updateAmount = value => {
    setAmount(value);
  };

  const setCreditCardPayment = value => {
    if (value) {
      setPaymentBy('credit_card');
    }
  };

  const setWireTransferPayment = value => {
    if (value) {
      setPaymentBy('wire_transfer');
    }
  };

  const createInvoice = async () => {
    try {
      setAmountError(null);
      setStripeError(null);
      setDisableSubmit(true);
      const routeToHit = creditCardSelected
        ? routes.billing.invoices.chargeCreditCard
        : routes.billing.invoices.create;
      const { data } = await httpClient.post(routeToHit({ affiliate_id: affiliateId }), {
        invoice: {
          amount,
          currency,
          credit_card_id: creditCardSelected ? selectedCardId : null,
        },
      });

      setDisableSubmit(false);
      if (data.redirectUrl) {
        window.location.replace(data.redirectUrl);
      }
      if (data.requiresAction && data.challengeIframeSourceUrl) {
        setIframeSource(data.challengeIframeSourceUrl);
        setPaymentIntentId(data.paymentIntentId);
      }
    } catch (error) {
      setDisableSubmit(false);
      const response = error.response;
      if (response && response.status === 422) {
        setAmountError(response.data.error.amount);
        setStripeError(response.data.error.stripeError);
      } else {
        throw error;
      }
    }
  };

  const newBalance = parseFloat(currentBalance) + parseFloat(amount || 0);

  const messageListener = async event => {
    if (event.data.id !== paymentIntentId) {
      return;
    }

    const postUrl = event.data.postUrl;

    if (postUrl) {
      setIsLoading(true);
      window.removeEventListener('message', messageListener, true);
      const { data } = await httpClient.post(postUrl);

      if (data.status !== stripeSuccessStatus && data.error) {
        setStripeError(data.error);
        setIsLoading(false);
      } else if (data.status === stripeSuccessStatus && data.redirectUrl) {
        window.location.replace(data.redirectUrl);
      }
    }
  };

  useEffect(() => {
    if (!modalOpen) {
      setAmountError(null);
      setStripeError(null);
      setPaymentBy('');
      setAmount('');
      setIframeSource(null);
      setPaymentIntentId(null);
      if (primaryCreditCard) {
        setSelectedCardId(primaryCreditCard.id);
      } else {
        setSelectedCardId(null);
      }
      window.removeEventListener('message', messageListener, true);
    }
  }, [modalOpen]);

  useEffect(() => {
    if (primaryCreditCard && selectedCardId === null) {
      setSelectedCardId(primaryCreditCard.id);
    }
  }, [primaryCreditCard]);

  useEffect(() => {
    if (iframeSource && paymentIntentId) {
      window.addEventListener('message', messageListener, true);
    }
  }, [iframeSource, paymentIntentId]);

  return (
    <>
      <Button
        className="button button--v2 button--small button--primary modal-toggle"
        version="v2"
        size="small"
        fullWidth={true}
        type="primary"
        label={<I18nText id="billing.invoices.top_up" />}
        onClick={() => {
          setModalOpen(true);
        }}
      />
      <Modal
        className="modal__wrapper modal__wrapper-small"
        isVisible={modalOpen}
        onOutsideClick={() => setModalOpen(false)}
      >
        <>
          <div className="modal__header">
            <Modal.Title>
              <I18nText id="billing.top_up.header" />
            </Modal.Title>
            <div
              onClick={() => setModalOpen(false)}
              onKeyDown={() => setModalOpen(false)}
              role="presentation"
            >
              <Icon name="close" size="normal" />
            </div>
          </div>
          <Modal.Content>
            {iframeSource ? (
              <div className="col-grid direction-column">
                <div className="col-6 col-bleed-x credit-card-details__billing-form-title">
                  {I18n.t('components.billing.add_credit_card.step2_test_top_up')}
                </div>
                <div className="col-5 col-bleed credit-card-details__billing-form-info">
                  {I18n.t('components.billing.add_credit_card.step2_test_info')}
                </div>
                <Alert isVisible={!!stripeError} type="danger" hideClose={true}>
                  {stripeError}
                </Alert>

                <div className="billing__top-up-modal-iframe-container-parent">
                  <div
                    id="stripe-three-ds-in-payment-container"
                    className="billing__top-up-modal-iframe-container"
                  >
                    <iframe
                      title="3dsFrame"
                      src={iframeSource}
                      className="billing__top-up-modal-iframe"
                      referrerPolicy="origin-when-cross-origin"
                    />
                  </div>
                  {isLoading && (
                    <div className="col-grid col-bleed direction-row align-center">
                      <Spinner color="black" size="small" bgColor="neutral" />
                      <span className="billing__top-up-modal-iframe-spinner">
                        {I18n.t('components.billing.add_credit_card.spinner_text')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="col-12 col-bleed">
                  <Text level={2}>
                    <I18nText id="billing.top_up.current_balance" />:{' '}
                    <Amount value={currentBalance} currency={currency} />
                  </Text>
                  <Text level={1} weight="bold">
                    <I18nText id="billing.top_up.payment_by" />
                  </Text>
                  <Text level={1}>
                    {disableCreditCardOption ? (
                      <Tooltip
                        content={<I18nText id="billing.top_up.disabled_info" />}
                        position="right"
                        type="warning"
                        showArrow={true}
                        gap={30}
                        arrowWidth={10}
                      >
                        <Radio
                          label={<I18nText id="billing.top_up.credit_card" />}
                          disabled={disableCreditCardOption}
                          name="credit-card"
                          size="small"
                          onChange={event => setCreditCardPayment(event.target.checked)}
                          checked={creditCardSelected}
                        />
                      </Tooltip>
                    ) : (
                      <Radio
                        label={<I18nText id="billing.top_up.credit_card" />}
                        name="credit-card"
                        size="small"
                        onChange={event => setCreditCardPayment(event.target.checked)}
                        checked={creditCardSelected}
                      />
                    )}
                    <br />
                    <Radio
                      label={<I18nText id="billing.top_up.wire_transfer" />}
                      name="wire-transfer"
                      size="small"
                      onChange={event => setWireTransferPayment(event.target.checked)}
                      checked={wireTransferSelected}
                    />
                  </Text>
                  <Input
                    label={null}
                    placeholder={I18n.t('components.billing.top_up.placeholders.amount')}
                    required={true}
                    name="amount"
                    value={amount}
                    onChange={updateAmount}
                    type="number"
                    touched={amount !== '' || !!amountError}
                    error={amountError}
                  />
                  <Text level={2}>
                    <I18nText id="billing.top_up.new_balance" />:{' '}
                    <Amount value={newBalance} currency={currency} />
                  </Text>
                  <Alert isVisible={wireTransferSelected} type="warning" hideClose={true}>
                    <I18nText id="billing.top_up.delayed_transaction_explanation" />
                  </Alert>
                  <Alert
                    className="billing__top-up-modal-selected-card"
                    isVisible={creditCardSelected && stripeError}
                    type="danger"
                    hideClose={true}
                  >
                    {stripeError}
                  </Alert>
                  {selectedCard && creditCardSelected && (
                    <div className="billing__top-up-modal-selected-card">
                      <div className="billing__top-up-modal-selected-card-title">
                        <I18nText id="billing.top_up.selected_card" />
                      </div>
                      <CreditCard
                        id={selectedCard.id}
                        lastFour={selectedCard.lastFour}
                        cardType={selectedCard.cardType}
                        cardholderName={selectedCard.cardholderName}
                        disableAction={true}
                        asSelected={true}
                        affiliateId={affiliateId}
                        primary={selectedCard.primary}
                      />
                    </div>
                  )}
                  {creditCardSelected && otherCards.length > 0 && (
                    <>
                      <div className="billing__top-up-modal-other-cards-title">
                        <I18nText id="billing.top_up.other_cards" />
                      </div>
                      <div className="billing__credit-card-secondary-cards">
                        {otherCards.map(card => (
                          <div
                            role="presentation"
                            onClick={() => setSelectedCardId(card.id)}
                            className="billing__top-up-modal-other-card"
                          >
                            <CreditCard
                              id={card.id}
                              lastFour={card.lastFour}
                              cardType={card.cardType}
                              cardholderName={card.cardholderName}
                              disableAction={true}
                              asSelected={false}
                              affiliateId={affiliateId}
                              primary={card.primary}
                              clickable={true}
                            />
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </Modal.Content>
          <Modal.Footer>
            <div className="col-12 col-bleed-x">
              <Button
                version="v2"
                type="primary"
                size="small"
                label={<I18nText id="billing.top_up.create_payment" />}
                onClick={createInvoice}
                disabled={paymentBy === '' || disableSubmit}
              />
              <Button
                version="v2"
                size="small"
                label={I18n.t('shared.action.cancel')}
                onClick={() => setModalOpen(false)}
              />
            </div>
          </Modal.Footer>
        </>
      </Modal>
    </>
  );
};

TopUpModal.defaultProps = {
  primaryCreditCard: null,
  creditCards: [],
};

TopUpModal.propTypes = {
  currentBalance: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  affiliateId: PropTypes.number.isRequired,
  disableCreditCardOption: PropTypes.bool.isRequired,
  primaryCreditCard: PropTypes.shape({
    id: PrimaryShapeStringAndNumber.isRequired,
    lastFour: PropTypes.string.isRequired,
    cardType: PropTypes.string.isRequired,
    cardholderName: PropTypes.string.isRequired,
    primary: PrimaryShapeStringAndBool.isRequired,
  }),
  creditCards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PrimaryShapeStringAndNumber.isRequired,
      lastFour: PropTypes.string.isRequired,
      cardType: PropTypes.string.isRequired,
      cardholderName: PropTypes.string.isRequired,
      primary: PrimaryShapeStringAndBool.isRequired,
    }),
  ),
  stripeSuccessStatus: PropTypes.string.isRequired,
};

export default TopUpModal;
